import React, { useState } from 'react';
import { Link, useParams, Navigate, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { ModuleManager } from '../../../components/admin/courses/ModuleManager';
import { useCourse } from '../../../hooks/admin/useCourse';
import { useAuth } from '../../../context/AuthContext';
import { LoadingScreen } from '../../../components/common/LoadingScreen';
import { toast } from 'react-hot-toast';
import { updateCourseModules } from '../../../services/admin/courseModuleService';

export function EditCoursePage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { course, isLoading, error } = useCourse(courseId);
  const { isAdmin } = useAuth();
  const [isSaving, setIsSaving] = useState(false);

  const handleSuccess = () => {
    navigate('/admin/courses');
  };

  if (isLoading) {
    return (
      <LoadingScreen />
    );
  }

  if (error) {
    toast.error('Failed to load course');
    return <Navigate to="/admin/courses" replace />;
  }

  // Check both authentication and admin status
  if (!isAdmin && !isLoading) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleModulesChange = async (updatedModules) => {
    setIsSaving(true);
    try {
      await updateCourseModules(courseId, updatedModules);
      toast.success('Course modules updated successfully');
    } catch (error) {
      console.error('Error updating modules:', error);
      toast.error('Failed to update modules');
    } finally {
      setIsSaving(false);
    }
  };

  if (!course) {
    return (
      <div className="min-h-screen bg-background-dark py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-2xl font-bold text-white">Course not found</h1>
          <Link to="/admin/courses" className="text-accent-green hover:opacity-80 mt-4 inline-block">
            Back to Courses
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-dark py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-6">
          <Link
            to="/admin/courses"
            className="inline-flex items-center text-accent-green hover:opacity-80"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Courses
          </Link>
        </div>

        <div className="bg-background-card rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-6">
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-2xl font-bold text-white">
                {course.title} - Module Management
              </h1>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-400">
                  {isSaving ? 'Saving...' : 'All changes are saved automatically'}
                </span>
              </div>
            </div>
            
            <ModuleManager
              courseId={course.id}
              modules={course.modules || []}
              onModulesChange={handleModulesChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
}